import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Globe, ChevronDown, ChevronRight, MapPin, Flag,
  Umbrella, Sun, Leaf, Mountain, Building, Music,
  PalmtreeIcon, Languages
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { usePhrases } from '@/context/PhrasesContext';

// Regional dialect configuration
const ENGLISH_DIALECTS = [
  { id: 'british', name: 'British', flag: '🇬🇧', icon: Umbrella, color: 'blue' },
  { id: 'irish', name: 'Irish', flag: '🇮🇪', icon: Leaf, color: 'emerald' },
  { id: 'aussie', name: 'Australian', flag: '🇦🇺', icon: Sun, color: 'orange' },
  { id: 'newzealand', name: 'New Zealand', flag: '🇳🇿', icon: Mountain, color: 'teal' },
  { id: 'canadian', name: 'Canadian', flag: '🇨🇦', icon: Leaf, color: 'red' },
  { id: 'caribbean', name: 'Caribbean', flag: '🏝️', icon: PalmtreeIcon, color: 'cyan' },
  { id: 'southafrican', name: 'South African', flag: '🇿🇦', icon: Sun, color: 'green' },
  { id: 'african', name: 'African', flag: '🌍', icon: Globe, color: 'amber' },
  { id: 'asian', name: 'Asian', flag: '🌏', icon: Globe, color: 'rose' },
  { id: 'eastcoast', name: 'US East Coast', flag: '🗽', icon: Building, color: 'slate' },
  { id: 'westcoast', name: 'US West Coast', flag: '🌴', icon: Sun, color: 'purple' },
  { id: 'midwest', name: 'US Midwest', flag: '🌾', icon: MapPin, color: 'amber' },
  { id: 'southern', name: 'US Southern', flag: '🍑', icon: MapPin, color: 'orange' },
  { id: 'deepsouth', name: 'US Deep South', flag: '🎸', icon: Music, color: 'indigo' },
  { id: 'texas', name: 'Texas', flag: '🤠', icon: MapPin, color: 'red' },
];

const SPANISH_DIALECTS = [
  { id: 'es-mexico', name: 'México', flag: '🇲🇽', icon: MapPin, color: 'green' },
  { id: 'es-spain', name: 'España', flag: '🇪🇸', icon: Flag, color: 'red' },
  { id: 'es-southamerica', name: 'Sudamérica', flag: '🌎', icon: Globe, color: 'blue' },
  { id: 'es-centralamerica', name: 'Centroamérica', flag: '🌎', icon: MapPin, color: 'teal' },
  { id: 'es-puertorico', name: 'Puerto Rico', flag: '🇵🇷', icon: PalmtreeIcon, color: 'cyan' },
  { id: 'es-texas', name: 'Texas (Español)', flag: '🤠', icon: MapPin, color: 'orange' },
  { id: 'es-california', name: 'California', flag: '🌴', icon: Sun, color: 'yellow' },
  { id: 'es-newmexico', name: 'Nuevo México', flag: '🏜️', icon: Sun, color: 'amber' },
  { id: 'es-canary-islands', name: 'Islas Canarias', flag: '🇮🇨', icon: PalmtreeIcon, color: 'purple' },
  { id: 'es-easter-island', name: 'Isla de Pascua', flag: '🗿', icon: Mountain, color: 'slate' },
  { id: 'es-guinea', name: 'Guinea Ecuatorial', flag: '🇬🇶', icon: Globe, color: 'green' },
  { id: 'es-chavacano', name: 'Chavacano', flag: '🇵🇭', icon: Languages, color: 'rose' },
  { id: 'es-islands', name: 'Caribbean Spanish', flag: '🏝️', icon: PalmtreeIcon, color: 'cyan' },
];

// Color classes
const colorClasses = {
  'blue': 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20',
  'emerald': 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20',
  'orange': 'bg-orange-500/10 text-orange-600 hover:bg-orange-500/20',
  'teal': 'bg-teal-500/10 text-teal-600 hover:bg-teal-500/20',
  'red': 'bg-red-500/10 text-red-600 hover:bg-red-500/20',
  'cyan': 'bg-cyan-500/10 text-cyan-600 hover:bg-cyan-500/20',
  'green': 'bg-green-500/10 text-green-600 hover:bg-green-500/20',
  'amber': 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20',
  'rose': 'bg-rose-500/10 text-rose-600 hover:bg-rose-500/20',
  'slate': 'bg-slate-500/10 text-slate-600 hover:bg-slate-500/20',
  'purple': 'bg-purple-500/10 text-purple-600 hover:bg-purple-500/20',
  'indigo': 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20',
  'yellow': 'bg-yellow-500/10 text-yellow-600 hover:bg-yellow-500/20',
};

export const RegionalDialectSelector = ({ onSelectDialect }) => {
  const navigate = useNavigate();
  const { t, isSpanish } = useLanguage();
  const { phrases } = usePhrases();
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Get phrase count for a category
  const getCount = (categoryId) => {
    return phrases.filter(p => p.category === categoryId).length;
  };
  
  const handleDialectClick = (dialectId) => {
    if (onSelectDialect) {
      onSelectDialect(dialectId);
    } else {
      navigate(`/?dialect=${dialectId}`);
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  return (
    <Card className="p-4 sm:p-6 bg-gradient-to-br from-primary/5 to-secondary/5 border-2" data-testid="regional-dialect-selector">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="h-5 w-5 text-primary" />
        <h3 className="font-semibold text-lg">
          {isSpanish ? 'Explorar por Dialecto Regional' : 'Browse by Regional Dialect'}
        </h3>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        {isSpanish 
          ? 'Selecciona una región para ver frases específicas de ese dialecto'
          : 'Select a region to view phrases specific to that dialect'}
      </p>
      
      {/* English Dialects Section */}
      <div className="mb-4">
        <Button
          variant="ghost"
          className="w-full justify-between p-3 h-auto hover:bg-primary/10"
          onClick={() => toggleSection('english')}
          data-testid="english-dialects-toggle"
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">🇬🇧</span>
            <span className="font-medium">
              {isSpanish ? 'Dialectos del Inglés' : 'English Dialects'}
            </span>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {ENGLISH_DIALECTS.length}
            </span>
          </div>
          {expandedSection === 'english' ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
        
        {expandedSection === 'english' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mt-3 pl-2">
            {ENGLISH_DIALECTS.map((dialect) => {
              const count = getCount(dialect.id);
              const IconComponent = dialect.icon;
              return (
                <button
                  key={dialect.id}
                  onClick={() => handleDialectClick(dialect.id)}
                  className={`flex flex-col items-center p-3 rounded-lg transition-all ${colorClasses[dialect.color]} cursor-pointer`}
                  data-testid={`dialect-${dialect.id}`}
                >
                  <span className="text-2xl mb-1">{dialect.flag}</span>
                  <span className="text-xs font-medium text-center leading-tight">{dialect.name}</span>
                  {count > 0 && (
                    <span className="text-[10px] text-muted-foreground mt-0.5">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Spanish Dialects Section */}
      <div>
        <Button
          variant="ghost"
          className="w-full justify-between p-3 h-auto hover:bg-primary/10"
          onClick={() => toggleSection('spanish')}
          data-testid="spanish-dialects-toggle"
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">🇪🇸</span>
            <span className="font-medium">
              {isSpanish ? 'Dialectos del Español' : 'Spanish Dialects'}
            </span>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
              {SPANISH_DIALECTS.length}
            </span>
          </div>
          {expandedSection === 'spanish' ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
        
        {expandedSection === 'spanish' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mt-3 pl-2">
            {SPANISH_DIALECTS.map((dialect) => {
              const count = getCount(dialect.id);
              const IconComponent = dialect.icon;
              return (
                <button
                  key={dialect.id}
                  onClick={() => handleDialectClick(dialect.id)}
                  className={`flex flex-col items-center p-3 rounded-lg transition-all ${colorClasses[dialect.color]} cursor-pointer`}
                  data-testid={`dialect-${dialect.id}`}
                >
                  <span className="text-2xl mb-1">{dialect.flag}</span>
                  <span className="text-xs font-medium text-center leading-tight">{dialect.name}</span>
                  {count > 0 && (
                    <span className="text-[10px] text-muted-foreground mt-0.5">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default RegionalDialectSelector;
